// ============================================================
// characterFolderImport - what `CharacterFolderView` decides, pure.
//
// Which version of each character to read (a pin beats the build
// label, the label beats the latest), which room a staging went to,
// and the one-line report of what happened. No React and no file
// reading, so each can be tested without a folder.
// ============================================================
import { pickByLabel } from '../utils/folderFiles';
import type { FileCharacter } from '../utils/folderFiles';
import { charPath } from './workshop/CharacterShelf';

/** What a folder import does with what it read. */
export type FolderImportMode = 'import' | 'stage' | 'load';

/** A waiting room the folder can stage into. */
interface RoomChoice {
  id: string;
  name: string;
}

/** The file the build label alone would give this character (its latest when nothing matches). */
export function labelledVersion(character: FileCharacter, label: string): string {
  return pickByLabel([character], label)[0].file.name;
}

/**
 * WHICH VERSION OF EACH. A pin wins over the label, the label wins over
 * the latest - most specific first, which is the only order that lets
 * one character break ranks with the rest. `pinned` is keyed by shelf
 * path. `fellBack` counts the characters a label found nothing for.
 */
export function resolveVersions(entries: FileCharacter[], label: string, pinned: Record<string, string>) {
  const picks = pickByLabel(entries, label);
  const resolved = picks.map((p) => {
    const pin = pinned[charPath(p.character.group, p.character.name)];
    const chosen = pin ? (p.character.iterations.find((f) => f.name === pin) ?? p.file) : p.file;
    return { ...p.character, file: chosen };
  });
  const fellBack = label ? picks.filter((p) => !p.matched).length : 0;
  return { resolved, fellBack };
}

/**
 * Where a staging went, for the report. `staged` is what `onStage`
 * returned - it may have just made the room, so it is the only one that
 * knows (v8.38.0); failing that, the room the chooser named.
 */
export function stagedWhere(staged: string | null | void, roomId: string, rooms: RoomChoice[]): string {
  if (typeof staged === 'string') return `→ ${staged}`;
  if (roomId.startsWith('new:')) return '→ a new room';
  return `→ ${rooms.find((r) => r.id === roomId)?.name ?? 'the active room'}`;
}

const VERBS: Record<FolderImportMode, string> = { import: 'Imported', stage: 'Staged', load: 'Loaded' };

interface NoteInput {
  mode: FolderImportMode;
  count: number;
  /** "the folder name", "the selection" or a group's name. */
  what: string;
  /** `stagedWhere`'s answer when staging, else empty. */
  where: string;
  failed: string[];
  warnings: number;
  fellBack: number;
  label: string;
}

/**
 * The report is the point. A folder of twenty with one bad file used to
 * import nothing and say nothing; now nineteen arrive and the twentieth
 * is named.
 */
export function folderImportNote({ mode, count, what, where, failed, warnings, fellBack, label }: NoteInput): string {
  const parts = [`${VERBS[mode]} ${count} from ${what} ${where}`.trim()];
  if (failed.length) parts.push(`could not read ${failed.join(', ')}`);
  if (warnings) parts.push(`${warnings} needed fixing`);
  if (fellBack) parts.push(`${fellBack} had no “${label}” and came at their latest`);
  return parts.join(' · ');
}
